import { faPlus, faStar, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	EmailApiService,
	EmailDomainService,
	type DomainSummary,
	type EmailTemplate,
} from '@supply-mover-2-0/core';
import {
	AgentConfigSource,
	type AgentConfigFieldDefinition,
} from '@supply-mover-2-0/shared';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ConversationAgentSelector } from '../../components/forms/entity-selectors/ConversationAgentSelector';
import { UserSelector } from '../../components/forms/entity-selectors/UserSelector';
import { Input } from '../../components/forms/Input';
import {
	SelectSearch,
	type SelectSearchGroup,
} from '../../components/forms/SelectSearch';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import { AgentService } from '../services/agentService';

const emailApiService = new EmailApiService();

interface AgentToolConfigFieldProps {
	fieldName: string;
	fieldDefinition: AgentConfigFieldDefinition;
	value: unknown;
	onChange: (value: unknown) => void;
	error?: string;
}

/**
 * Parse an email address into local part and domain
 */
function parseEmailAddress(email: string): {
	localPart: string;
	domain: string;
} {
	if (email == null || email === '') {
		return { localPart: 'noreply', domain: '' };
	}

	const atIndex = email.lastIndexOf('@');

	if (atIndex > 0) {
		return {
			localPart: email.substring(0, atIndex),
			domain: email.substring(atIndex + 1),
		};
	}

	return { localPart: email, domain: '' };
}

/**
 * Renders a configuration field for agent tools based on the AgentConfigSource.
 * Supports: PROVISIONED_PHONES, VERIFIED_EMAIL_DOMAINS, EMAIL_TEMPLATES, TEXT, USERS, AGENTS
 */
export function AgentToolConfigField({
	fieldName,
	fieldDefinition,
	value,
	onChange,
	error,
}: AgentToolConfigFieldProps): React.ReactNode {
	const colors = useThemeColors();
	const { t } = useTranslation();

	// Data loading state
	const [provisionedPhones, setProvisionedPhones] = useState<
		Array<{ phoneNumber: string }>
	>([]);
	const [phonesLoading, setPhonesLoading] = useState(false);
	const [verifiedDomains, setVerifiedDomains] = useState<Array<DomainSummary>>(
		[],
	);
	const [domainsLoading, setDomainsLoading] = useState(false);
	const [emailTemplates, setEmailTemplates] = useState<Array<EmailTemplate>>(
		[],
	);
	const [templatesLoading, setTemplatesLoading] = useState(false);

	// Email local part state (for email address composition)
	const { localPart: parsedLocalPart, domain: parsedDomain } = useMemo(
		() => parseEmailAddress(String(value ?? '')),
		[value],
	);
	const [localPart, setLocalPart] = useState(parsedLocalPart);
	const [selectedDomain, setSelectedDomain] = useState(parsedDomain);

	// Sync local part and domain when value changes externally
	useEffect(() => {
		const parsed = parseEmailAddress(String(value ?? ''));
		setLocalPart(parsed.localPart);
		setSelectedDomain(parsed.domain);
	}, [value]);

	// Load data based on source
	useEffect(() => {
		if (fieldDefinition.source === AgentConfigSource.PROVISIONED_PHONES) {
			setPhonesLoading(true);
			AgentService.getProvisionedPhones()
				.then((result) => {
					setProvisionedPhones(result.data);
				})
				.catch(() => {
					setProvisionedPhones([]);
				})
				.finally(() => {
					setPhonesLoading(false);
				});
		} else if (
			fieldDefinition.source === AgentConfigSource.VERIFIED_EMAIL_DOMAINS
		) {
			setDomainsLoading(true);
			EmailDomainService.listDomains()
				.then((result) => {
					setVerifiedDomains(result.domains.filter((d) => d.verified));
				})
				.catch(() => {
					setVerifiedDomains([]);
				})
				.finally(() => {
					setDomainsLoading(false);
				});
		} else if (fieldDefinition.source === AgentConfigSource.EMAIL_TEMPLATES) {
			setTemplatesLoading(true);
			emailApiService
				.getEmailTemplates({ limit: 100 })
				.then((result) => {
					setEmailTemplates(
						result.data.filter((tmpl) => tmpl.isActive !== false),
					);
				})
				.catch(() => {
					setEmailTemplates([]);
				})
				.finally(() => {
					setTemplatesLoading(false);
				});
		}
	}, [fieldDefinition.source]);

	// Auto-select primary domain if no domain is selected
	useEffect(() => {
		if (
			fieldDefinition.source === AgentConfigSource.VERIFIED_EMAIL_DOMAINS &&
			selectedDomain === '' &&
			verifiedDomains.length > 0
		) {
			const primaryDomain = verifiedDomains.find((d) => d.primary);
			const domainToSelect = primaryDomain ?? verifiedDomains[0];

			if (domainToSelect != null) {
				setSelectedDomain(domainToSelect.domain);
				const newEmail = `${localPart}@${domainToSelect.domain}`;
				onChange(newEmail);
			}
		}
	}, [
		verifiedDomains,
		selectedDomain,
		localPart,
		onChange,
		fieldDefinition.source,
	]);

	const handleLocalPartChange = (newLocalPart: string): void => {
		setLocalPart(newLocalPart);

		if (selectedDomain !== '') {
			onChange(`${newLocalPart}@${selectedDomain}`);
		}
	};

	const handleDomainChange = (newDomain: string): void => {
		setSelectedDomain(newDomain);

		if (newDomain !== '') {
			onChange(`${localPart}@${newDomain}`);
		} else {
			onChange('');
		}
	};

	// Render based on source type
	const renderInput = (): React.ReactNode => {
		switch (fieldDefinition.source) {
			case AgentConfigSource.PROVISIONED_PHONES: {
				const phoneGroups: Array<SelectSearchGroup> = [
					{
						key: 'provisioned-phones',
						label: t('Provisioned Phone Numbers'),
						options: provisionedPhones.map((phone) => ({
							value: phone.phoneNumber,
							label: phone.phoneNumber,
						})),
					},
				];

				const hasPhones = provisionedPhones.length > 0;

				return (
					<div>
						<SelectSearch
							groups={phoneGroups}
							value={String(value ?? '')}
							onChange={(newValue: string) => {
								onChange(newValue ?? '');
							}}
							placeholder={
								phonesLoading
									? t('Loading...')
									: hasPhones
										? t('Select a phone number')
										: t('No phone numbers available')
							}
							disabled={phonesLoading || !hasPhones}
							emptyMessage={t('No phone numbers found')}
							mode="static"
						/>
						{!hasPhones && !phonesLoading && (
							<div className={`text-xs mt-1 ${colors.classes.textSecondary}`}>
								{t(
									'No provisioned phone numbers available. Contact your administrator to provision phone numbers.',
								)}
							</div>
						)}
					</div>
				);
			}

			case AgentConfigSource.VERIFIED_EMAIL_DOMAINS: {
				// Sort domains: primary first, then alphabetically
				const sortedDomains = [...verifiedDomains].sort((a, b) => {
					if (a.primary && !b.primary) {
						return -1;
					}

					if (!a.primary && b.primary) {
						return 1;
					}

					return a.domain.localeCompare(b.domain);
				});

				const domainGroups: Array<SelectSearchGroup> = [
					{
						key: 'verified-domains',
						label: t('Verified Domains'),
						options: sortedDomains.map((domain) => ({
							value: domain.domain,
							label: domain.primary
								? `${domain.domain} (${t('Primary')})`
								: domain.domain,
							icon: domain.primary ? faStar : undefined,
						})),
					},
				];

				const hasDomains = verifiedDomains.length > 0;

				return (
					<div className="space-y-2">
						<div className="flex items-center gap-2">
							{/* Local Part Input */}
							<div className="flex-1">
								<Input
									value={localPart}
									onChange={(e) => {
										handleLocalPartChange(e.target.value);
									}}
									placeholder={t('noreply')}
									disabled={domainsLoading || !hasDomains}
								/>
							</div>

							{/* @ Symbol */}
							<span
								className={`text-lg font-medium ${colors.classes.textSecondary}`}
							>
								@
							</span>

							{/* Domain Dropdown */}
							<div className="flex-[2]">
								<SelectSearch
									groups={domainGroups}
									value={selectedDomain}
									onChange={(newValue: string) => {
										handleDomainChange(newValue ?? '');
									}}
									placeholder={
										domainsLoading
											? t('Loading...')
											: hasDomains
												? t('Select a domain')
												: t('No domains available')
									}
									disabled={domainsLoading || !hasDomains}
									emptyMessage={t('No domains found')}
									mode="static"
								/>
							</div>
						</div>

						{!hasDomains && !domainsLoading && (
							<div className={`text-xs ${colors.classes.textSecondary}`}>
								{t(
									'No verified email domains available. Configure domains in Settings > Email Domains.',
								)}
							</div>
						)}
					</div>
				);
			}

			case AgentConfigSource.EMAIL_TEMPLATES: {
				const templateGroups: Array<SelectSearchGroup> = [
					{
						key: 'email-templates',
						label: t('Email Templates'),
						options: emailTemplates.map((template) => ({
							value: String(template.id),
							label: template.name,
							description: template.description,
						})),
					},
				];

				const hasTemplates = emailTemplates.length > 0;

				return (
					<div>
						<SelectSearch
							groups={templateGroups}
							value={String(value ?? '')}
							onChange={(newValue: string) => {
								onChange(newValue != null ? Number(newValue) : null);
							}}
							placeholder={
								templatesLoading
									? t('Loading...')
									: hasTemplates
										? t('Select an email template')
										: t('No templates available')
							}
							disabled={templatesLoading || !hasTemplates}
							emptyMessage={t('No email templates found')}
							mode="static"
						/>
						{!hasTemplates && !templatesLoading && (
							<div className={`text-xs mt-1 ${colors.classes.textSecondary}`}>
								{t(
									'No email templates available. Create templates in Settings > Email Templates.',
								)}
							</div>
						)}
					</div>
				);
			}

			case AgentConfigSource.TRANSFER_TARGETS:
				return (
					<TransferTargetsEditor
						value={value}
						onChange={onChange}
						colors={colors}
						t={t}
					/>
				);

			case AgentConfigSource.CHAT_TRANSFER_TARGETS:
				return (
					<ChatTransferTargetsEditor
						value={value}
						onChange={onChange}
						colors={colors}
						t={t}
					/>
				);

			case AgentConfigSource.IVR_MENU:
				return (
					<IvrMenuEditor
						value={value}
						onChange={onChange}
						colors={colors}
						t={t}
					/>
				);

			case AgentConfigSource.TEXT:
				// Simple text input
				return (
					<Input
						value={String(value ?? '')}
						onChange={(e) => {
							onChange(e.target.value);
						}}
						placeholder={fieldDefinition.label}
					/>
				);

			case AgentConfigSource.USERS:
			case AgentConfigSource.AGENTS:
				// TODO: Implement user and agent selectors when needed
				return (
					<div className={`text-sm ${colors.classes.textSecondary}`}>
						{t('User/Agent selector not yet implemented')}
					</div>
				);

			default:
				// Default text input for unknown sources
				return (
					<Input
						value={String(value ?? '')}
						onChange={(e) => {
							onChange(e.target.value);
						}}
						placeholder={fieldDefinition.label}
					/>
				);
		}
	};

	return (
		<div className="space-y-2">
			<label className={`text-sm font-medium ${colors.classes.textPrimary}`}>
				{t(fieldDefinition.label)}{' '}
				{fieldDefinition.required && (
					<span className={colors.classes.statusError}>*</span>
				)}
			</label>
			{fieldDefinition.description != null && (
				<div className={`text-xs ${colors.classes.textSecondary}`}>
					{t(fieldDefinition.description)}
				</div>
			)}
			{renderInput()}
			{error != null && (
				<div className={`text-xs ${colors.classes.statusError}`}>{error}</div>
			)}
		</div>
	);
}

interface TransferTarget {
	number: string;
	label: string;
}

interface TransferTargetsEditorProps {
	value: unknown;
	onChange: (value: unknown) => void;
	colors: ReturnType<typeof useThemeColors>;
	t: (key: string) => string;
}

/**
 * List editor for transfer target phone numbers.
 * Each row has a phone number input and a label text input, with add/remove controls.
 * Stored value: JSON string of Array<{ number: string, label: string }>
 */
function TransferTargetsEditor({
	value,
	onChange,
	colors,
	t,
}: TransferTargetsEditorProps): React.ReactNode {
	const parseTargets = useCallback((): Array<TransferTarget> => {
		if (value == null || value === '') {
			return [];
		}

		try {
			const parsed = typeof value === 'string' ? JSON.parse(value) : value;

			if (Array.isArray(parsed)) {
				return parsed.filter(
					(item: unknown): item is TransferTarget =>
						typeof item === 'object' &&
						item != null &&
						typeof (item as TransferTarget).number === 'string' &&
						typeof (item as TransferTarget).label === 'string',
				);
			}
		} catch {
			// Invalid JSON — start fresh
		}

		return [];
	}, [value]);

	const [targets, setTargets] = useState<Array<TransferTarget>>(parseTargets);

	// Sync when external value changes
	useEffect(() => {
		setTargets(parseTargets());
	}, [parseTargets]);

	const emitChange = useCallback(
		(updated: Array<TransferTarget>): void => {
			setTargets(updated);
			onChange(JSON.stringify(updated));
		},
		[onChange],
	);

	const handleAdd = useCallback((): void => {
		emitChange([...targets, { number: '', label: '' }]);
	}, [targets, emitChange]);

	const handleRemove = useCallback(
		(index: number): void => {
			const updated = targets.filter((_, i) => i !== index);
			emitChange(updated);
		},
		[targets, emitChange],
	);

	const handleFieldChange = useCallback(
		(index: number, field: keyof TransferTarget, newValue: string): void => {
			const updated = targets.map((target, i) =>
				i === index ? { ...target, [field]: newValue } : target,
			);
			emitChange(updated);
		},
		[targets, emitChange],
	);

	return (
		<div className="space-y-2">
			{targets.map((target, index) => (
				<div key={index} className="flex items-center gap-2">
					<div className="flex-1">
						<Input
							value={target.label}
							onChange={(e) => {
								handleFieldChange(index, 'label', e.target.value);
							}}
							placeholder={t('Label (e.g., Billing Dept)')}
						/>
					</div>
					<div className="flex-1">
						<Input
							value={target.number}
							onChange={(e) => {
								handleFieldChange(index, 'number', e.target.value);
							}}
							placeholder={t('Phone number (e.g., +15551234567)')}
						/>
					</div>
					<button
						type="button"
						onClick={() => {
							handleRemove(index);
						}}
						className={`p-2 rounded hover:bg-danger-badge ${colors.classes.statusError}`}
						title={t('Remove')}
					>
						<FontAwesomeIcon icon={faTrash} className="w-3.5 h-3.5" />
					</button>
				</div>
			))}

			<button
				type="button"
				onClick={handleAdd}
				className={`flex items-center gap-1.5 text-sm ${colors.classes.textSecondary} hover:${colors.classes.textPrimary}`}
			>
				<FontAwesomeIcon icon={faPlus} className="w-3 h-3" />
				{t('Add Transfer Target')}
			</button>

			{targets.length === 0 && (
				<div className={`text-xs ${colors.classes.textSecondary}`}>
					{t(
						'No transfer targets configured. Add at least one phone number for the agent to transfer calls to.',
					)}
				</div>
			)}
		</div>
	);
}

// --- Chat Transfer Targets Editor ---

interface ChatTransferTarget {
	key: string;
	kind: 'user' | 'aiAgent';
	id: number;
	label: string;
}

interface ChatTransferTargetsEditorProps {
	value: unknown;
	onChange: (value: unknown) => void;
	colors: ReturnType<typeof useThemeColors>;
	t: (key: string) => string;
}

/**
 * List editor for chat-conversation transfer destinations
 * (`AgentConfigSource.CHAT_TRANSFER_TARGETS`).
 *
 * Heterogeneous compared to the voice `TransferTargetsEditor`:
 * each row picks a destination KIND (user or AI agent) and uses
 * the matching entity selector to choose the id. Label auto-fills
 * from the picker's record on initial selection but stays
 * editable so admins can override (e.g. "Alice (Sales)" instead
 * of just "Alice"). Key stays editable so admins can pick
 * LLM-friendly identifiers (e.g. `tier2-support`,
 * `alice-billing`).
 *
 * Stored value: JSON string of Array<ChatTransferTarget>. Matches
 * the shape `TransferConversationTool._parseTransferTargets`
 * accepts (which also reads array-typed values directly so legacy
 * agents whose config was hand-pasted as plain JSON still work).
 */
function ChatTransferTargetsEditor({
	value,
	onChange,
	colors,
	t,
}: ChatTransferTargetsEditorProps): React.ReactNode {
	const parseTargets = useCallback((): Array<ChatTransferTarget> => {
		if (value == null || value === '') {
			return [];
		}
		try {
			const parsed = typeof value === 'string' ? JSON.parse(value) : value;
			if (Array.isArray(parsed)) {
				return parsed.filter(
					(item: unknown): item is ChatTransferTarget =>
						typeof item === 'object' &&
						item != null &&
						typeof (item as ChatTransferTarget).key === 'string' &&
						typeof (item as ChatTransferTarget).label === 'string' &&
						typeof (item as ChatTransferTarget).id === 'number' &&
						((item as ChatTransferTarget).kind === 'user' ||
							(item as ChatTransferTarget).kind === 'aiAgent'),
				);
			}
		} catch {
			// Invalid JSON — start fresh
		}
		return [];
	}, [value]);

	const [targets, setTargets] =
		useState<Array<ChatTransferTarget>>(parseTargets);

	useEffect(() => {
		setTargets(parseTargets());
	}, [parseTargets]);

	const emitChange = useCallback(
		(updated: Array<ChatTransferTarget>): void => {
			setTargets(updated);
			onChange(JSON.stringify(updated));
		},
		[onChange],
	);

	const handleAdd = useCallback((): void => {
		emitChange([...targets, { key: '', kind: 'user', id: 0, label: '' }]);
	}, [targets, emitChange]);

	const handleRemove = useCallback(
		(index: number): void => {
			emitChange(targets.filter((_, i) => i !== index));
		},
		[targets, emitChange],
	);

	const handleFieldChange = useCallback(
		<K extends keyof ChatTransferTarget>(
			index: number,
			field: K,
			newValue: ChatTransferTarget[K],
		): void => {
			emitChange(
				targets.map((target, i) =>
					i === index ? { ...target, [field]: newValue } : target,
				),
			);
		},
		[targets, emitChange],
	);

	// When the kind flips, the previously-selected entity id no
	// longer applies — reset id and label so the row is in a
	// consistent state for the new picker.
	const handleKindChange = useCallback(
		(index: number, newKind: 'user' | 'aiAgent'): void => {
			emitChange(
				targets.map((target, i) =>
					i === index ? { ...target, kind: newKind, id: 0, label: '' } : target,
				),
			);
		},
		[targets, emitChange],
	);

	const handleEntitySelected = useCallback(
		(index: number, id: number | null, record: unknown): void => {
			if (id == null) {
				handleFieldChange(index, 'id', 0);
				return;
			}
			const recordWithName = record as
				| {
						name?: string;
						firstName?: string;
						lastName?: string;
						email?: string;
				  }
				| null
				| undefined;
			let derivedLabel = '';
			if (recordWithName != null) {
				if (recordWithName.name != null) {
					derivedLabel = recordWithName.name;
				} else if (recordWithName.firstName != null) {
					derivedLabel =
						`${recordWithName.firstName} ${recordWithName.lastName ?? ''}`.trim();
				} else if (recordWithName.email != null) {
					derivedLabel = recordWithName.email;
				}
			}
			emitChange(
				targets.map((target, i) =>
					i === index
						? {
								...target,
								id,
								// Only auto-fill the label when it's blank — once
								// the admin types something custom, don't stomp it
								// on a re-select.
								label: target.label.trim() === '' ? derivedLabel : target.label,
							}
						: target,
				),
			);
		},
		[targets, emitChange, handleFieldChange],
	);

	return (
		<div className="space-y-2">
			{targets.map((target, index) => (
				<div
					key={index}
					className={`p-3 rounded border ${colors.classes.borderDefault}`}
				>
					<div className="flex items-center gap-2 mb-2">
						<select
							value={target.kind}
							onChange={(e) => {
								handleKindChange(index, e.target.value as 'user' | 'aiAgent');
							}}
							className={`text-sm rounded border px-2 py-1 ${colors.classes.borderDefault} ${colors.classes.bgPrimary} ${colors.classes.textPrimary}`}
						>
							<option value="user">{t('User')}</option>
							<option value="aiAgent">{t('AI Agent')}</option>
						</select>
						<div className="flex-1">
							{target.kind === 'user' ? (
								<UserSelector
									value={target.id > 0 ? target.id : null}
									onChange={(id, record) => {
										handleEntitySelected(index, id, record);
									}}
									placeholder={t('Select user...')}
									allowClear
								/>
							) : (
								<ConversationAgentSelector
									value={target.id > 0 ? target.id : null}
									onChange={(id, record) => {
										handleEntitySelected(index, id, record);
									}}
									placeholder={t('Select AI agent...')}
									allowClear
								/>
							)}
						</div>
						<button
							type="button"
							onClick={() => {
								handleRemove(index);
							}}
							className={`p-2 rounded hover:bg-danger-badge ${colors.classes.statusError}`}
							title={t('Remove')}
						>
							<FontAwesomeIcon icon={faTrash} className="w-3.5 h-3.5" />
						</button>
					</div>
					<div className="grid grid-cols-2 gap-2">
						<div>
							<label
								className={`block text-xs mb-1 ${colors.classes.textSecondary}`}
							>
								{t('Key (LLM picks by this)')}
							</label>
							<Input
								value={target.key}
								onChange={(e) => {
									handleFieldChange(index, 'key', e.target.value);
								}}
								placeholder={t('e.g. tier2-support, alice-billing')}
							/>
						</div>
						<div>
							<label
								className={`block text-xs mb-1 ${colors.classes.textSecondary}`}
							>
								{t('Label (audit + LLM context)')}
							</label>
							<Input
								value={target.label}
								onChange={(e) => {
									handleFieldChange(index, 'label', e.target.value);
								}}
								placeholder={t('e.g. Tier 2 Support (AI)')}
							/>
						</div>
					</div>
				</div>
			))}

			<button
				type="button"
				onClick={handleAdd}
				className={`flex items-center gap-1.5 text-sm ${colors.classes.textSecondary} hover:${colors.classes.textPrimary}`}
			>
				<FontAwesomeIcon icon={faPlus} className="w-3 h-3" />
				{t('Add Transfer Target')}
			</button>

			{targets.length === 0 && (
				<div className={`text-xs ${colors.classes.textSecondary}`}>
					{t(
						'No transfer targets configured. The AI cannot transfer this conversation to anyone until at least one destination is added.',
					)}
				</div>
			)}
		</div>
	);
}

// --- IVR Menu Editor ---

type IvrActionType = 'transfer';

interface IvrMenuOption {
	ivr: number;
	label: string;
	phrase: string;
	action: IvrActionType;
	actionConfig: Record<string, unknown>;
	confirmationMessage?: string;
}

const IVR_ACTION_OPTIONS: Array<{ value: IvrActionType; label: string }> = [
	{ value: 'transfer', label: 'Transfer' },
];

const DTMF_KEYS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];

interface IvrMenuEditorProps {
	value: unknown;
	onChange: (value: unknown) => void;
	colors: ReturnType<typeof useThemeColors>;
	t: (key: string) => string;
}

/**
 * List editor for IVR menu options.
 * Each row configures a DTMF key, voice phrase, action type, and action-specific config.
 * Stored value: JSON string of Array<IvrMenuOption>
 */
function IvrMenuEditor({
	value,
	onChange,
	colors,
	t,
}: IvrMenuEditorProps): React.ReactNode {
	const parseOptions = useCallback((): Array<IvrMenuOption> => {
		if (value == null || value === '') {
			return [];
		}

		try {
			const parsed = typeof value === 'string' ? JSON.parse(value) : value;

			if (Array.isArray(parsed)) {
				return parsed.filter(
					(item: unknown): item is IvrMenuOption =>
						typeof item === 'object' &&
						item != null &&
						typeof (item as IvrMenuOption).ivr === 'number' &&
						typeof (item as IvrMenuOption).label === 'string' &&
						typeof (item as IvrMenuOption).action === 'string',
				);
			}
		} catch {
			// Invalid JSON — start fresh
		}

		return [];
	}, [value]);

	const [options, setOptions] = useState<Array<IvrMenuOption>>(parseOptions);

	useEffect(() => {
		setOptions(parseOptions());
	}, [parseOptions]);

	const emitChange = useCallback(
		(updated: Array<IvrMenuOption>): void => {
			setOptions(updated);
			onChange(JSON.stringify(updated));
		},
		[onChange],
	);

	const handleAdd = useCallback((): void => {
		// Pick the first unused DTMF key
		const usedKeys = new Set(options.map((o) => o.ivr));
		const nextKey = DTMF_KEYS.find((k) => !usedKeys.has(k)) ?? 0;

		emitChange([
			...options,
			{
				ivr: nextKey,
				label: '',
				phrase: '',
				action: 'transfer',
				actionConfig: { targetNumber: '', targetLabel: '' },
			},
		]);
	}, [options, emitChange]);

	const handleRemove = useCallback(
		(index: number): void => {
			emitChange(options.filter((_, i) => i !== index));
		},
		[options, emitChange],
	);

	const handleFieldChange = useCallback(
		(index: number, field: keyof IvrMenuOption, newValue: unknown): void => {
			const updated = options.map((opt, i) => {
				if (i !== index) {
					return opt;
				}

				// When action type changes, reset actionConfig to appropriate defaults
				if (field === 'action') {
					return {
						...opt,
						action: newValue as IvrActionType,
						actionConfig:
							newValue === 'transfer'
								? { targetNumber: '', targetLabel: '' }
								: {},
					};
				}

				return { ...opt, [field]: newValue };
			});
			emitChange(updated);
		},
		[options, emitChange],
	);

	const handleActionConfigChange = useCallback(
		(index: number, configKey: string, configValue: string): void => {
			const updated = options.map((opt, i) =>
				i === index
					? {
							...opt,
							actionConfig: { ...opt.actionConfig, [configKey]: configValue },
						}
					: opt,
			);
			emitChange(updated);
		},
		[options, emitChange],
	);

	return (
		<div className="space-y-3">
			{options.map((option, index) => (
				<div
					key={index}
					className={`p-3 rounded-lg border ${colors.classes.borderDefault} space-y-2`}
				>
					{/* Row 1: DTMF Key, Label, Phrase */}
					<div className="flex items-center gap-2">
						<div className="w-20">
							<label
								className={`text-xs ${colors.classes.textSecondary} block mb-1`}
							>
								{t('DTMF Key')}
							</label>
							<select
								value={option.ivr}
								onChange={(e) => {
									handleFieldChange(index, 'ivr', Number(e.target.value));
								}}
								className={`w-full rounded border px-2 py-1.5 text-sm ${colors.classes.borderDefault} ${colors.classes.bgPrimary} ${colors.classes.textPrimary}`}
							>
								{DTMF_KEYS.map((key) => (
									<option key={key} value={key}>
										{key}
									</option>
								))}
							</select>
						</div>
						<div className="flex-1">
							<label
								className={`text-xs ${colors.classes.textSecondary} block mb-1`}
							>
								{t('Label')}
							</label>
							<Input
								value={option.label}
								onChange={(e) => {
									handleFieldChange(index, 'label', e.target.value);
								}}
								placeholder={t('e.g., Billing Department')}
							/>
						</div>
						<div className="flex-1">
							<label
								className={`text-xs ${colors.classes.textSecondary} block mb-1`}
							>
								{t('Voice Phrase')}
							</label>
							<Input
								value={option.phrase}
								onChange={(e) => {
									handleFieldChange(index, 'phrase', e.target.value);
								}}
								placeholder={t('e.g., billing')}
							/>
						</div>
						<div className="pt-5">
							<button
								type="button"
								onClick={() => {
									handleRemove(index);
								}}
								className={`p-2 rounded hover:bg-danger-badge ${colors.classes.statusError}`}
								title={t('Remove')}
							>
								<FontAwesomeIcon icon={faTrash} className="w-3.5 h-3.5" />
							</button>
						</div>
					</div>

					{/* Row 2: Action type + action-specific config */}
					<div className="flex items-end gap-2">
						<div className="w-32">
							<label
								className={`text-xs ${colors.classes.textSecondary} block mb-1`}
							>
								{t('Action')}
							</label>
							<select
								value={option.action}
								onChange={(e) => {
									handleFieldChange(
										index,
										'action',
										e.target.value as IvrActionType,
									);
								}}
								className={`w-full rounded border px-2 py-1.5 text-sm ${colors.classes.borderDefault} ${colors.classes.bgPrimary} ${colors.classes.textPrimary}`}
							>
								{IVR_ACTION_OPTIONS.map((act) => (
									<option key={act.value} value={act.value}>
										{t(act.label)}
									</option>
								))}
							</select>
						</div>

						{/* Transfer-specific fields */}
						{option.action === 'transfer' && (
							<>
								<div className="flex-1">
									<label
										className={`text-xs ${colors.classes.textSecondary} block mb-1`}
									>
										{t('Target Number')}
									</label>
									<Input
										value={String(option.actionConfig['targetNumber'] ?? '')}
										onChange={(e) => {
											handleActionConfigChange(
												index,
												'targetNumber',
												e.target.value,
											);
										}}
										placeholder={t('+15551234567')}
									/>
								</div>
								<div className="flex-1">
									<label
										className={`text-xs ${colors.classes.textSecondary} block mb-1`}
									>
										{t('Target Label')}
									</label>
									<Input
										value={String(option.actionConfig['targetLabel'] ?? '')}
										onChange={(e) => {
											handleActionConfigChange(
												index,
												'targetLabel',
												e.target.value,
											);
										}}
										placeholder={t('e.g., Billing')}
									/>
								</div>
							</>
						)}
					</div>

					{/* Row 3: Confirmation message (optional) */}
					<div>
						<label
							className={`text-xs ${colors.classes.textSecondary} block mb-1`}
						>
							{t('Confirmation Message')}
							<span className={`ml-1 ${colors.classes.textSecondary}`}>
								({t('optional')})
							</span>
						</label>
						<Input
							value={option.confirmationMessage ?? ''}
							onChange={(e) => {
								handleFieldChange(index, 'confirmationMessage', e.target.value);
							}}
							placeholder={t('e.g., Transferring you to billing now...')}
						/>
					</div>
				</div>
			))}

			<button
				type="button"
				onClick={handleAdd}
				className={`flex items-center gap-1.5 text-sm ${colors.classes.textSecondary} hover:${colors.classes.textPrimary}`}
			>
				<FontAwesomeIcon icon={faPlus} className="w-3 h-3" />
				{t('Add IVR Option')}
			</button>

			{options.length === 0 && (
				<div className={`text-xs ${colors.classes.textSecondary}`}>
					{t(
						'No IVR options configured. Add menu options to enable DTMF-driven routing.',
					)}
				</div>
			)}
		</div>
	);
}

export default AgentToolConfigField;
